import React, {
  createContext,
  useState,
  useEffect,
  useMemo,
  useRef
} from 'react';
import { io } from 'socket.io-client';
import { useNotif } from 'src/hooks';
import { useSelector, useDispatch } from 'react-redux';
import { setStatus } from 'src/redux/slices/wf';
import { logout } from 'src/redux/slices/auth';
import { useNavigate } from 'react-router';
import { defaultNotifOptions } from 'src/constants';
import { EventEmitter } from 'events';

// Create the context
const SocketContext = createContext(null);
const SOCKETS = [
  'connect',
  'message',
  'command',
  'error',
  'disconnect',
  'connect_error'
];

//settings
const socketURL = window.location.origin;

// Create the provider
const SocketProvider = ({ children }) => {
  const initialized = useRef(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { success, info, error, custom } = useNotif();
  const [currentBiz, setCurrentBiz] = useState(null);
  const [currentSandbox, setCurrentSandbox] = useState(null);
  const [joinedRooms, setJoinedRooms] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const { userToken, current_user, ixBiz, sandbox } = useSelector(
    state => state.socketio
  );

  const [socket, setSocket] = useState(null);
  const { frontEnd } = useSelector(({ bizMeta }) => bizMeta);
  const isSocketAllowed = frontEnd?.socket?.allow ?? false;
  const storeNotifications = frontEnd?.socket?.store_notifications ?? false;
  const specificUserNotif = frontEnd?.socket?.specific_user_notif ?? true;
  const logData = frontEnd?.socket?.log_data ?? false;

  const socketRelay = useMemo(() => new EventEmitter(), []);
  //socket options
  const extraHeaders = {
    Authorization: userToken,
    username: current_user?.username ?? null
  };
  const query = { token: userToken, username: current_user?.username ?? null };

  //functions for commands
  const COMMANDLOGIC = {
    WF_Update: data => {
      const {
        message,
        linkText,
        path,
        timestamp,
        jid,
        wfStatus,
        jStatus,
        username,
        assignedTo
      } = data;

      dispatch(
        setStatus({
          jid: jid,
          wfStatus: wfStatus,
          jStatus: jStatus
        })
      );
      const isRelevant =
        !specificUserNotif ||
        (username !== current_user.username &&
          (Array.isArray(assignedTo) ? assignedTo : []).includes(
            current_user.username
          ));

      if (!isRelevant) return;

      custom({
        text: message,
        variant: 'info',
        linkText: linkText,
        path: path,
        notifKey: timestamp,
        options: defaultNotifOptions
      });
    },
    Batch_WF_Update: data => {
      const { message = '', username } = data;
      const isRelevant =
        !specificUserNotif && username !== current_user.username;
      if (isRelevant) {
        custom({
          text: message,
          variant: 'info',
          options: defaultNotifOptions
        });
      }

      // Apply updates for each jid
      data.jid.forEach(jid => {
        dispatch(
          setStatus({ jid, wfStatus: data.wfStatus, jStatus: data.jStatus })
        );
      });
    },
    JStatus_Update: data => {
      dispatch(
        setStatus({
          jid: data.jid,
          wfStatus: data.wfStatus,
          jStatus: data.jStatus
        })
      );
    },
    Assigned_Update: data => {
      const isRelevant =
        !specificUserNotif ||
        (data.username !== current_user.username &&
          (Array.isArray(data.userList) ? data.userList : []).includes(
            current_user.username
          ));

      if (isRelevant) {
        const { message, linkText, path, timestamp } = data;

        custom({
          text: message,
          variant: 'info',
          linkText: linkText,
          path: path,
          notifKey: timestamp,
          options: defaultNotifOptions
        });
      }
    },
    Logout: data => {
      if (data.targetUser == current_user.username) {
        dispatch(logout());
        navigate('/app/login');
      }
    },
    __default__: data => {
      const typeLower = (data?.type ?? '').toLowerCase();
      if (!typeLower || typeLower === 'general') return; // skip general
      if (logData) console.log('Dynamic relay for type:', data.type, data);
      socketRelay.emit(data.type, data);
    }
  };
  //functions for listeners
  const LISTENERS = {
    connect: () => {
      setIsConnected(true);
      if (logData) console.log('Socket Connected');
    },
    command: data => {
      if (logData) {
        console.log('Received Command', data);
      }
      const timestamp = Date.now();
      data.timestamp = data?.timestamp ?? timestamp;
      StoreCommandsReceived(data);
      if (COMMANDLOGIC[data.type]) {
        COMMANDLOGIC[data.type](data);
      } else {
        COMMANDLOGIC.__default__(data);
      }
    },
    message: data => {
      if (logData) {
        console.log('Received Message', data);
      }
      if (!data) return;

      const match = data.match(/^user (\w+) joined room (\w+_\w+)(?:\[\d+\])?/);
      if (match) {
        const [_, username, fullRoom] = match;
        const roomType = fullRoom.split('_')[0]; // e.g., 'payroll'

        // Skip general rooms
        if (roomType.toLowerCase() === 'general') return;
        if (current_user.username == username) {
          socketRelay.emit(roomType, {
            command: 'joinedRoom',
            username,
            room: fullRoom
          });
        }
      }
    },
    error: data => {
      error(data);
    },
    disconnect: reason => {
      setIsConnected(false);
      if (logData) console.log('Socket Disconnected:', reason);
    },
    connect_error: err => {
      setIsConnected(false);
      if (logData) console.error('Connection Error:', err);
    }
  };
  // localstorage
  function StoreCommandsReceived(ReceivedData) {
    const MAX_LENGTH = 30;

    // Get the raw message and trim if needed
    const rawMessage = ReceivedData?.message ?? ReceivedData?.msg ?? '';
    // Early return if there's literally no message
    if (!rawMessage || rawMessage.trim() === '') return;

    let message =
      rawMessage.length > MAX_LENGTH
        ? rawMessage.slice(0, MAX_LENGTH) + '...'
        : rawMessage;

    // If it's a chat message and the username exists, prepend it
    if (ReceivedData?.type === 'chat' && ReceivedData?.username) {
      const username = ReceivedData.username.trim();
      if (username) {
        message = `${username}: ${message}`;
      }
    }
    const data = {
      jStatus: ReceivedData?.jStatus ?? '',
      message: message ?? '',
      path: ReceivedData?.path ?? '',
      linkText: ReceivedData?.linkText ?? '',
      subText: ReceivedData?.subText ?? '',
      type: ReceivedData?.type ?? ''
    };
    if (!storeNotifications || data?.message == '' || data?.type == 'chat') {
      return;
    }

    const isAssignedToCurrentUser =
      ReceivedData?.username !== current_user.username &&
      ['assignedTo', 'userList'].some(key => {
        const value = ReceivedData?.[key];
        if (!value) return false;

        if (Array.isArray(value)) {
          // Array case
          return value
            .map(v => v.trim().toLowerCase())
            .includes(current_user.username.toLowerCase());
        }

        if (typeof value === 'string') {
          // String case (single or comma-separated)
          return value
            .split(',')
            .map(v => v.trim().toLowerCase())
            .includes(current_user.username.toLowerCase());
        }

        return false;
      });

    const isAllowedToSave =
      (!specificUserNotif && ReceivedData?.type !== 'Assigned_Update') ||
      (ReceivedData?.type === 'chat' &&
        current_user.username?.toLowerCase() !==
          ReceivedData.username.toLowerCase());

    if (isAllowedToSave || isAssignedToCurrentUser) {
      const now = ReceivedData?.timestamp ?? Date.now();
      let storedCommandsReceived =
        JSON.parse(localStorage.getItem('CommandsReceived')) || {};

      const lastTimestamp = Math.max(
        ...Object.keys(storedCommandsReceived).map(Number),
        0
      );

      if (now - lastTimestamp < 300) return; // throttle too-frequent saves

      const timestamps = Object.keys(storedCommandsReceived)
        .map(Number)
        .sort((a, b) => a - b);
      if (timestamps.length >= 5) {
        delete storedCommandsReceived[timestamps[0]]; // remove oldest
      }

      storedCommandsReceived[now] = {
        data,
        read: false,
        biz: ixBiz ?? ''
      };

      localStorage.setItem(
        'CommandsReceived',
        JSON.stringify(storedCommandsReceived)
      );
      window.dispatchEvent(new Event('CommandsUpdated'));
    }
  }

  //socket connection initialization
  useEffect(() => {
    if (!isSocketAllowed) {
      return;
    }
    if (!current_user.username && !userToken) {
      if (socket) {
        socket.disconnect(); // 🔹 Disconnect if state becomes invalid
        setSocket(null);
      }
      return;
    }

    if (socket) return;

    const testSocket = io(socketURL, {
      query: query,
      extraHeaders: extraHeaders,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000
      // transports: ['websocket'], // force websocket only
      // reconnection: false // no retry
    });

    SOCKETS.forEach(item => {
      testSocket.on(item, data => {
        LISTENERS[item](data);
      });
    });

    setSocket(testSocket);
    // setIsConnected(true);
    if (ixBiz && current_user && isSocketAllowed) {
      if (currentBiz === null) {
        setCurrentBiz(ixBiz);
      }
      if (currentBiz !== ixBiz || currentSandbox !== sandbox) {
        const room = 'general[' + sandbox + ']';
        leaveRoom(room);
        setCurrentBiz(ixBiz);
        setCurrentSandbox(sandbox);
      }
      testSocket.emit('join_room', {
        ixBiz: ixBiz,
        room: 'general[' + sandbox + ']',
        username: current_user.username
      });
    }

    return () => {
      const room = 'general[' + sandbox + ']';
      leaveAllRooms();
      if (testSocket) {
        testSocket.disconnect();
        SOCKETS.forEach(item => {
          testSocket.off(item);
        });
      }
    };
  }, [userToken, isSocketAllowed, current_user]);

  // Function to join a room
  const joinRoom = room => {
    if (socket) {
      if (isSocketAllowed) {
        socket.emit('join_room', {
          ixBiz: ixBiz,
          room: room + '[' + sandbox + ']',
          username: current_user.username
        });
        setJoinedRooms(prev => [...new Set([...prev, room])]);
      }
    }
  };

  // Function to leave a room
  const leaveRoom = room => {
    if (ixBiz && room && socket) {
      socket.emit('leave_room', {
        ixBiz: ixBiz,
        room: room + '[' + sandbox + ']'
      });
      setJoinedRooms(prev => prev.filter(r => r !== room));
    }
  };

  // Functions to send command/data
  // data = object
  // required ixBiz, username, room
  const sendMessage = data => {
    if (socket) {
      let dataToSend = { ixBiz };
      if (data?.room) {
        dataToSend = {
          ...dataToSend,
          username: current_user.username,
          room: data?.room + '[' + sandbox + ']',
          ...data
        };
      } else {
        dataToSend = {
          ...dataToSend,
          username: current_user.username,
          room: 'general[' + sandbox + ']',
          ...data
        };
      }
      if (logData) {
        console.log('sendMessage', dataToSend);
      }
      socket.emit('send_data', dataToSend);
    }
  };
  const sendCommand = data => {
    if (socket) {
      let dataToSend = { ixBiz };
      if (data?.room) {
        dataToSend = {
          ...dataToSend,
          ...data,

          username: current_user.username,
          room: data?.room + '[' + sandbox + ']'
        };
      } else {
        dataToSend = {
          ...dataToSend,
          ...data,
          username: current_user.username,
          room: 'general[' + sandbox + ']'
        };
      }
      if (logData) {
        console.log('SendCommand', dataToSend);
      }
      socket.emit('send_command', dataToSend);
    }
  };

  function Disconnect() {
    if (socket) {
      socket.disconnect();
    }
  }
  function RemoteLogout() {
    if (socket) {
      const data = {
        type: 'Logout',
        targetUser: current_user.username
      };
      let dataToSend = { ixBiz };
      if (data?.room) {
        dataToSend = {
          ...dataToSend,
          ...data,
          username: current_user.username,
          room: data.room + '[' + sandbox + ']'
        };
      } else {
        dataToSend = {
          ...dataToSend,
          ...data,
          username: current_user.username,
          room: 'general[' + sandbox + ']'
        };
      }
      socket.emit('send_command', dataToSend);
    }
  }
  function leaveAllRooms() {
    joinedRooms.forEach(room => {
      const type = room.split('_')[0];

      // Command everyone in the room to share their username
      sendCommand({
        type: type,
        command: 'requestRoomUsers',
        room: room
      });

      leaveRoom(room);
    });
    setJoinedRooms([]);
  }

  useEffect(() => {
    if (isConnected == false && initialized.current == true) {
      error('Disconnected');
    } else if (initialized.current == true && isConnected == true) {
      success('Reconnected');
    } else if (initialized.current == false && isConnected == true) {
      initialized.current = true;
    }
  }, [isConnected]);
  return (
    <SocketContext.Provider
      value={{
        isConnected,
        socket,
        joinRoom,
        leaveRoom,
        sendMessage,
        sendCommand,
        Disconnect,
        RemoteLogout,
        socketRelay,
        leaveAllRooms
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};
export { SocketProvider };
export default SocketContext;
